import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Copy, Loader2, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ordersApi, type ErpIntegrationDetails } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ErpIntegrationSheetProps {
  /** Pedido aberto no painel; nulo mantém o painel fechado. */
  order: { id: string; orderNumber: string } | null;
  onClose: () => void;
}

type StepStatus = "ok" | "error" | "info";

interface TimelineStep {
  key: string;
  at: string | null;
  title: string;
  status: StepStatus;
  durationMs?: number | null;
  request?: string | null;
  response?: string | null;
  message?: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  sending: "Enviando ao ERP",
  sent: "Aceito pelo ERP",
  rejected: "Recusado",
  failed: "Aguardando nova tentativa",
  gave_up: "Tentativas automáticas esgotadas",
  shadow: "Modo sombra (nada foi enviado)",
};

const STATUS_COLORS: Record<string, string> = {
  sending: "bg-sky-100 text-sky-800",
  sent: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  failed: "bg-amber-100 text-amber-800",
  gave_up: "bg-red-100 text-red-800",
  shadow: "bg-gray-200 text-gray-800",
};

// Mesmas cores da tela do Linkapi: verde = ok, vermelho = erro, azul = informativo.
const STEP_COLORS: Record<StepStatus, string> = {
  ok: "bg-emerald-500",
  error: "bg-red-500",
  info: "bg-sky-500",
};

const RESEND_STATUSES = ["rejected", "failed", "gave_up"];

const SAO_PAULO = "America/Sao_Paulo";

const formatDay = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString("pt-BR", { timeZone: SAO_PAULO, day: "2-digit", month: "2-digit", year: "numeric" })
    : "-";

const formatTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleTimeString("pt-BR", { timeZone: SAO_PAULO, hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "";

const formatDateTime = (value?: string | null) => (value ? `${formatDay(value)} ${formatTime(value)}` : "-");

/** JSON identado quando o texto é JSON; senão devolve o texto como veio (ex.: resposta em texto puro do ERP). */
export const prettyJson = (text?: string | null) => {
  if (!text) return "";
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

const buildSteps = (details: ErpIntegrationDetails, attempt: number | null): TimelineStep[] => {
  const steps: TimelineStep[] = details.events
    .filter((event) => attempt == null || event.attempt === attempt)
    .map((event) => ({
      key: `event-${event.id}`,
      at: event.occurredAt,
      title: event.stepName,
      status: (event.status as StepStatus) ?? "info",
      durationMs: event.durationMs,
      request: event.requestJson,
      response: event.responseJson,
      message: event.errorMessage,
    }));

  // Retorno do ERP: status que as lambdas de status já gravaram para o pré-pedido.
  details.returns.forEach((item, index) => {
    const isRejection = /reprovado|cancelado/i.test(item.status);
    steps.push({
      key: `return-${index}`,
      at: item.at ?? null,
      title: `Retorno do ERP: ${item.status}`,
      status: isRejection ? "error" : "ok",
      message: item.salesId ? `Ordem de venda: ${item.salesId}` : null,
    });
  });

  if (details.noReturnAfterWait) {
    steps.push({
      key: "no-return",
      at: null,
      title: `Sem retorno do ERP há mais de ${details.returnWaitMinutes} min`,
      status: "error",
      message: "O ERP ainda não devolveu nenhum status deste pré-pedido. Confira no ERP antes de reenviar.",
    });
  }

  return steps;
};

function JsonBlock({ label, text }: { label: string; text: string }) {
  const content = prettyJson(text);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copiado para a área de transferência.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">{label}</h3>
        <Button type="button" variant="ghost" size="sm" className="gap-2" onClick={copy}>
          <Copy className="h-4 w-4" />
          Copiar
        </Button>
      </div>
      <pre className="max-h-[55vh] overflow-auto rounded-md bg-neutral-900 p-4 text-xs leading-5 text-neutral-100">
        {content}
      </pre>
    </div>
  );
}

/**
 * Andamento da integração direta do pedido com o ERP, no estilo da tela do Linkapi: à esquerda os passos com data e hora
 * (verde = ok, vermelho = erro, azul = informativo) e, à direita, o JSON enviado e a resposta do passo escolhido.
 * Os dados vêm de GET /orders/{id}/erp-integration (ErpDirectIntegrationFeature).
 */
export function ErpIntegrationSheet({ order, onClose }: ErpIntegrationSheetProps) {
  const queryClient = useQueryClient();
  const orderId = order?.id ?? null;

  // Tentativa escolhida pela pessoa; sem escolha (nulo), vale sempre a mais recente, inclusive quando chega uma nova.
  const [chosenAttempt, setChosenAttempt] = useState<number | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["orders", orderId, "erp-integration"],
    queryFn: () => ordersApi.getErpIntegration(orderId!),
    enabled: orderId != null,
    refetchInterval: orderId != null ? 15000 : false,
    retry: false,
  });

  const attempts = useMemo(
    () => Array.from(new Set((data?.events ?? []).map((event) => event.attempt))).sort((a, b) => a - b),
    [data],
  );

  const latestAttempt = attempts.length > 0 ? attempts[attempts.length - 1] : null;
  const attempt = chosenAttempt != null && attempts.includes(chosenAttempt) ? chosenAttempt : latestAttempt;

  // Ao abrir (ou fechar) o painel, volta a mostrar a tentativa mais recente.
  useEffect(() => {
    setChosenAttempt(null);
    setSelectedKey(null);
  }, [orderId]);

  const steps = useMemo(() => (data ? buildSteps(data, attempt) : []), [data, attempt]);
  const selected = steps.find((step) => step.key === selectedKey) ?? steps[steps.length - 1] ?? null;

  const canResend = data != null && !data.shadowOnly && RESEND_STATUSES.includes(data.status ?? "");

  const resend = async () => {
    if (!order) return;

    // Reenvio de administrador: apaga o histórico de status e devolve o pedido ao início do fluxo.
    const confirmed = window.confirm(
      "Reenviar o pedido ao ERP?\n\nO histórico de status deste pedido será apagado e ele volta ao início do fluxo. " +
        "Quem reenviou fica registrado no passo a passo.",
    );
    if (!confirmed) return;

    setResending(true);
    setChosenAttempt(null);
    setSelectedKey(null);
    try {
      await ordersApi.resendWebhook(order.orderNumber);
      toast.success("Pedido reenviado ao ERP.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "O ERP não aceitou o reenvio. Veja o passo a passo.";
      toast.error(message);
    } finally {
      setResending(false);
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ["orders", "all"] });
    }
  };

  return (
    <Sheet open={order != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[min(1100px,96vw)]">
        <SheetHeader className="space-y-1 border-b px-6 py-5 pr-12 text-left sm:text-left">
          <SheetTitle className="text-xl font-bold leading-tight">Integração do pedido #{order?.orderNumber}</SheetTitle>
          <SheetDescription className="text-sm">
            Passo a passo do envio do pedido ao ERP, com o JSON enviado e a resposta de cada etapa.
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-gray-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            Carregando andamento...
          </div>
        ) : isError || !data ? (
          <div className="flex flex-1 items-center justify-center p-6 text-center text-gray-600">
            Não foi possível carregar o andamento. A integração direta com o ERP pode estar desligada neste ambiente.
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col md:flex-row">
            <div className="w-full shrink-0 space-y-4 overflow-y-auto overflow-x-hidden border-b bg-gray-50 p-4 md:w-[clamp(320px,36%,460px)] md:border-b-0 md:border-r">
              <div className="space-y-2 rounded-md border bg-white p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-gray-700">Situação</span>
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-xs font-medium",
                      STATUS_COLORS[data.status ?? ""] ?? "bg-gray-100 text-gray-700",
                    )}
                  >
                    {data.status ? (STATUS_LABELS[data.status] ?? data.status) : "Ainda não enviado"}
                  </span>
                </div>
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs text-gray-600">
                  <dt>Nº de integração</dt>
                  <dd className="min-w-0 break-words text-right font-medium text-gray-900">{data.integrationNumber ?? "-"}</dd>
                  <dt>Pedido no ERP</dt>
                  <dd className="min-w-0 break-words text-right font-medium text-gray-900">{data.erpOrderNumber ?? "-"}</dd>
                  <dt>Tentativas</dt>
                  <dd className="min-w-0 break-words text-right font-medium text-gray-900">{data.attempts}</dd>
                  <dt>Envio agendado</dt>
                  <dd className="min-w-0 break-words text-right font-medium text-gray-900">{formatDateTime(data.webhookScheduledAt)}</dd>
                  {data.nextAttemptAt && (
                    <>
                      <dt>Próxima tentativa</dt>
                      <dd className="min-w-0 break-words text-right font-medium text-gray-900">{formatDateTime(data.nextAttemptAt)}</dd>
                    </>
                  )}
                </dl>

                {attempts.length > 1 && (
                  <div className="flex flex-wrap items-center gap-1 pt-1">
                    <span className="text-xs text-gray-600">Tentativa:</span>
                    {attempts.map((value) => (
                      <Button
                        key={value}
                        type="button"
                        size="sm"
                        variant={value === attempt ? "default" : "outline"}
                        className="h-6 px-2 text-xs"
                        onClick={() => {
                          // Escolher a mais recente volta a acompanhar as novas tentativas.
                          setChosenAttempt(value === latestAttempt ? null : value);
                          setSelectedKey(null);
                        }}
                      >
                        {value}
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              {data.shadowOnly && (
                <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Modo sombra: o pedido foi montado no formato do ERP, mas nada foi enviado. O envio seguiu pelo fluxo
                    atual (lambda e Magento).
                  </span>
                </div>
              )}

              {data.lastError && (
                <div className="break-words rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900 [overflow-wrap:anywhere]">{data.lastError}</div>
              )}

              {steps.length === 0 ? (
                <p className="text-sm text-gray-600">
                  Nenhum passo registrado.
                </p>
              ) : (
                <ol className="space-y-3">
                  {steps.map((step) => {
                    const isSelected = selected?.key === step.key;
                    return (
                      <li key={step.key} className="min-w-0">
                        <button
                          type="button"
                          onClick={() => setSelectedKey(step.key)}
                          className="flex w-full min-w-0 items-stretch gap-3 text-left"
                        >
                          <div
                            className={cn(
                              "flex w-[88px] shrink-0 flex-col items-center justify-center rounded-md px-2 py-3 text-center text-[11px] font-medium leading-tight text-white",
                              STEP_COLORS[step.status],
                            )}
                          >
                            <span>{formatDay(step.at)}</span>
                            <span>{formatTime(step.at)}</span>
                          </div>
                          <div
                            className={cn(
                              "flex min-w-0 flex-1 flex-col justify-center rounded-md border bg-white px-3 py-2 text-sm",
                              isSelected && "ring-2 ring-primary",
                            )}
                          >
                            <span className="break-words [overflow-wrap:anywhere]">{step.title}</span>
                            {step.durationMs != null && (
                              <span className="text-[11px] text-gray-500">{step.durationMs} ms</span>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            <div className="min-w-0 flex-1 space-y-4 overflow-y-auto p-5">
              {selected ? (
                <>
                  <h2 className="break-words text-lg font-semibold leading-tight [overflow-wrap:anywhere]">{selected.title}</h2>

                  {selected.message && (
                    <div
                      className={cn(
                        "break-words rounded-md border p-3 text-sm [overflow-wrap:anywhere]",
                        selected.status === "error"
                          ? "border-red-200 bg-red-50 text-red-900"
                          : "border-gray-200 bg-gray-50 text-gray-800",
                      )}
                    >
                      {selected.message}
                    </div>
                  )}

                  {selected.request && <JsonBlock label="Request" text={selected.request} />}
                  {selected.response && <JsonBlock label="Response" text={selected.response} />}

                  {!selected.message && !selected.request && !selected.response && (
                    <p className="text-sm text-gray-600">Este passo não tem payload nem resposta registrados.</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-600">Escolha um passo para ver os detalhes.</p>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t px-6 py-3">
          <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            Atualizar
          </Button>

          {canResend && (
            <Button type="button" size="sm" className="gap-2" onClick={resend} disabled={resending}>
              {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Reenviar ao ERP
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
